import React, { useEffect, useRef } from "react";

const toc = [
  ["Scalars & Points", "scalars-points"],
  ["Vectors", "vectors"],
  ["Adding Vectors", "adding-vectors"],
  ["Scaling Vectors", "scaling-vectors"],
  ["Relative & Situated Vectors", "relative-situated"],
  ["Points from Vectors", "points-from-vectors"],
  ["Linear Sums of Points", "linear-sums"],
  ["Vector Magnitude", "magnitude"],
  ["Squared Magnitude", "squared-magnitude"],
  ["Unit Vectors", "unit-vectors"],
  ["Distance Between Points", "distance"],
  ["Dot Product", "dot-product"],
  ["Cross Product", "cross-product"],
  ["Vector Spaces", "vector-spaces"],
  ["Euclidean Space", "euclidean"],
  ["Frames", "frames"],
  ["Coordinate Handedness", "handedness"],
];

const F = ({ children }) => (
  <div className="formula-block">
    {(Array.isArray(children) ? children : [children]).map((line, i) => (
      <div key={i} className="formula-row">{line}</div>
    ))}
  </div>
);

const NoteBox = ({ children }) => (
  <div className="note-box">
    <div className="note-label">Note</div>
    {children}
  </div>
);

const CGFundamentals = ({ renderToc }) => {
  const ref = useRef(null);

  useEffect(() => {
    const tryRender = () => {
      if (window.renderMathInElement && ref.current) {
        window.renderMathInElement(ref.current, {
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "$", right: "$", display: false },
          ],
          throwOnError: false,
        });
      } else {
        setTimeout(tryRender, 100);
      }
    };
    tryRender();
  }, []);

  useEffect(() => {
    if (renderToc) renderToc(toc);
  }, [renderToc]);

  return (
    <div ref={ref}>
      <h1>Computer Graphics — Prerequisite Concepts</h1>
      <p className="subtitle">by Jeff Wilson, Ph.D.</p>

      {/* ── SCALARS & POINTS ── */}
      <h2 id="scalars-points">Scalars &amp; Points</h2>

      <h3>Scalars</h3>
      <p>
        A <strong>scalar</strong> is a real number — represented on a computer
        as an integer, rational, or floating-point value. Scalars represent
        distances, times, areas, and similar quantities.
      </p>
      <ul>
        <li>
          Notation: lowercase or familiar constant —{" "}
          {"$a, b, x, y, s, t, 3, \\alpha, \\epsilon, \\pi$"}
        </li>
      </ul>

      <h3>Points</h3>
      <p>
        A <strong>point</strong> is a precise location in 2D or 3D space.
        Points describe geometric elements such as vertices of polygons, circle
        centres, or intersection results.
      </p>
      <ul>
        <li>
          Notation: uppercase, <em>not</em> bolded — {"$P, A, B$"}
        </li>
        <li>
          2D Cartesian {"$(P_x, P_y)$"} &emsp; Polar {"$(r, \\phi)$"}
        </li>
      </ul>

      {/* ── VECTORS ── */}
      <h2 id="vectors">Vectors</h2>
      <p>
        A <strong>vector</strong> encodes both a <em>direction</em> and a{" "}
        <em>magnitude</em> (length). Vectors can be 2D or 3D, expressed in
        Cartesian or polar coordinates.
      </p>
      <ul>
        <li>
          Notation: uppercase <strong>bold</strong> —{" "}
          {"$\\mathbf{U}, \\mathbf{V}, \\mathbf{AB}$"} or arrow{" "}
          {"$\\vec{U}$"}
        </li>
      </ul>

      {/* ── ADDING VECTORS ── */}
      <h2 id="adding-vectors">Adding Vectors</h2>
      <p>Vectors are added component-wise (conceptually: head-to-tail chaining).</p>
      <F>
        {[
          "$\\mathbf{C}=\\mathbf{A}+\\mathbf{B}=(A_x+B_x,\\;A_y+B_y)$",
          "Commutative: $\\mathbf{A}+\\mathbf{B}=\\mathbf{B}+\\mathbf{A}$",
          "Associative: $(\\mathbf{A}+\\mathbf{B})+\\mathbf{W}=\\mathbf{A}+(\\mathbf{B}+\\mathbf{W})$",
          "Identity: $\\mathbf{A}+\\mathbf{0}=\\mathbf{A}$ — Inverse: $\\mathbf{A}+(-\\mathbf{A})=\\mathbf{0}$",
        ]}
      </F>

      {/* ── SCALING VECTORS ── */}
      <h2 id="scaling-vectors">Scaling Vectors</h2>
      <p>
        Multiplying a vector by scalar {"$k$"} scales its length while
        preserving direction.
      </p>
      <F>
        {[
          "$k\\mathbf{A}$: same direction, length $|k|\\,\\|\\mathbf{A}\\|$",
          "Distributive: $k(\\mathbf{A}+\\mathbf{B})=k\\mathbf{A}+k\\mathbf{B}$",
          "Identity: $1\\cdot\\mathbf{A}=\\mathbf{A}$ — Annihilation: $0\\cdot\\mathbf{A}=\\mathbf{0}$",
        ]}
      </F>

      {/* ── RELATIVE & SITUATED VECTORS ── */}
      <h2 id="relative-situated">Relative &amp; Situated Vectors</h2>
      <h3>Relative Vector</h3>
      <p>
        The difference between two points is a <strong>relative vector</strong>
        . The point of origin is <em>not</em> preserved.
      </p>
      <F>{"$\\mathbf{AB}=B-A$"}</F>

      <h3>Situated Vector</h3>
      <p>
        Pair a point and a vector as a tuple{" "}
        {"$\\langle A,\\mathbf{AB}\\rangle$"} to retain positional information.
        Without this pairing, a vector is assumed to originate at the coordinate
        origin.
      </p>

      {/* ── POINTS FROM VECTORS ── */}
      <h2 id="points-from-vectors">Creating a Point from a Vector</h2>
      <p>Adding a vector to a point translates it:</p>
      <F>
        {[
          "$B=A+\\mathbf{V}$",
          "$B=A+\\mathbf{AB}$ (reconstitutes the endpoint of a situated vector)",
        ]}
      </F>

      {/* ── LINEAR SUMS ── */}
      <h2 id="linear-sums">Linear Sums of Points</h2>
      <p>A plain sum of points is generally invalid. Two exceptions:</p>
      <ul>
        <li>
          <strong>Weights sum to 1</strong> — valid interpolated point, e.g.
          midpoint {"$C=\\frac{A+B}{2}$"}
        </li>
        <li>
          <strong>Weights sum to 0</strong> — result is a vector
        </li>
      </ul>

      {/* ── MAGNITUDE ── */}
      <h2 id="magnitude">Vector Magnitude</h2>
      <p>The Euclidean length — derived from the Pythagorean theorem:</p>
      <F>{"$$\\|\\mathbf{V}\\|=\\sqrt{V_x^2+V_y^2+V_z^2}$$"}</F>

      {/* ── SQUARED MAGNITUDE ── */}
      <h2 id="squared-magnitude">Squared Magnitude</h2>
      <p>
        Skips the square root; valid for distance <em>comparisons</em>, not
        absolute distances.
      </p>
      <F>{"$$\\|\\mathbf{V}\\|^2=V_x^2+V_y^2+V_z^2$$"}</F>
      <NoteBox>
        Non-linear — never substitute squared magnitude where the actual
        distance value is required (e.g. lighting attenuation).
      </NoteBox>

      {/* ── UNIT VECTORS ── */}
      <h2 id="unit-vectors">Unit Vectors</h2>
      <p>
        A unit vector has length exactly 1. Normalisation preserves direction
        and discards magnitude.
      </p>
      <F>{"$$\\hat{\\mathbf{V}}=\\frac{\\mathbf{V}}{\\|\\mathbf{V}\\|}$$"}</F>
      <p>
        Notation: hat diacritic {"$\\hat{\\mathbf{V}}$"}. Frequently combined
        with dot products to compute angles between vectors.
      </p>

      {/* ── DISTANCE ── */}
      <h2 id="distance">Distance Between Two Points</h2>
      <p>Form the relative vector, then compute its magnitude:</p>
      <F>{"$$d(A,B)=\\|B-A\\|$$"}</F>

      {/* ── DOT PRODUCT ── */}
      <h2 id="dot-product">Dot Product (Inner Product)</h2>
      <p>
        Maps two vectors to a scalar. Operator: centre dot{" "}
        {"$(\\cdot)$"}.
      </p>
      <F>
        {[
          "2D: $\\mathbf{A}\\cdot\\mathbf{B}=A_xB_x+A_yB_y$",
          "3D: $\\mathbf{A}\\cdot\\mathbf{B}=A_xB_x+A_yB_y+A_zB_z$",
          "$$\\mathbf{A}\\cdot\\mathbf{B}=\\|\\mathbf{A}\\|\\|\\mathbf{B}\\|\\cos\\theta$$",
          "$\\mathbf{A}\\cdot\\mathbf{B}=0\\iff\\mathbf{A}\\perp\\mathbf{B}$ (or either is zero)",
          "Self-dot: $\\mathbf{V}\\cdot\\mathbf{V}=\\|\\mathbf{V}\\|^2$",
        ]}
      </F>
      <p>
        Commutative and distributive. Measures how much of{"  "}
        {"$\\mathbf{A}$"} lies along {"$\\mathbf{B}$"}.
      </p>

      <h3>Scalar and Vector Projection</h3>
      <F>
        {[
          "Scalar: $\\displaystyle\\operatorname{proj}_{\\mathbf{B}}\\mathbf{A}=\\frac{\\mathbf{A}\\cdot\\mathbf{B}}{\\|\\mathbf{B}\\|}$",
          "Vector: $\\displaystyle\\mathbf{P}=\\frac{\\mathbf{A}\\cdot\\mathbf{B}}{\\mathbf{B}\\cdot\\mathbf{B}}\\,\\mathbf{B}$",
          "Parametric: $\\mathbf{p}(t)=t\\mathbf{B},\\quad t=\\dfrac{\\mathbf{A}\\cdot\\mathbf{B}}{\\mathbf{B}\\cdot\\mathbf{B}}$",
        ]}
      </F>

      {/* ── CROSS PRODUCT ── */}
      <h2 id="cross-product">Cross Product (3D)</h2>
      <p>
        Returns a vector perpendicular to both inputs. Magnitude equals the
        area of the parallelogram they form.
      </p>
      <F>
        {[
          "$$\\mathbf{A}\\times\\mathbf{B}=(A_yB_z-A_zB_y,\\;A_zB_x-A_xB_z,\\;A_xB_y-A_yB_x)$$",
          "$$\\|\\mathbf{A}\\times\\mathbf{B}\\|=\\|\\mathbf{A}\\|\\|\\mathbf{B}\\|\\sin\\theta$$",
        ]}
      </F>
      <ul>
        <li>
          Direction determined by the <strong>right-hand rule</strong>{" "}
          (right-handed coordinate system)
        </li>
        <li>
          No direct 2D version — promote 2D vectors to 3D with {"$z=0$"}
        </li>
        <li>Uses: surface normals, signed angle detection, winding-order tests</li>
      </ul>

      {/* ── VECTOR SPACES ── */}
      <h2 id="vector-spaces">Vector Space</h2>
      <p>
        A collection of fixed-dimensionality vectors supporting addition and
        scalar multiplication with closure. CG uses 2D and 3D vector spaces.
      </p>

      {/* ── EUCLIDEAN ── */}
      <h2 id="euclidean">Euclidean Vector Space</h2>
      <p>
        Extends a vector space with a defined dot product, computable
        magnitudes, and inter-point distances. Nearly all computer graphics
        operates in 2D or 3D Euclidean Vector Spaces.
      </p>

      {/* ── FRAMES ── */}
      <h2 id="frames">Frames</h2>
      <p>
        An origin plus a set of basis vectors — defines a coordinate reference
        frame. Frames are commonly represented as transformation matrices.
      </p>
      <F>
        {[
          "2D Frame: origin $O$, bases $\\mathbf{X},\\mathbf{Y}$",
          "$P=O+x\\mathbf{X}+y\\mathbf{Y}$",
        ]}
      </F>
      <p>
        <strong>Orthonormal frames</strong> (orthogonal unit-length bases) are
        generally preferred. Non-uniform scaling breaks orthonormality.
      </p>

      {/* ── HANDEDNESS ── */}
      <h2 id="handedness">Coordinate System Handedness</h2>
      <p>
        3D spaces are either left-handed or right-handed. Once two orthogonal
        axes are chosen, the third points in one of two opposite directions.
      </p>
      <ul>
        <li>
          Cross product defined with the right-hand rule → right-handed systems
          are historically preferred
        </li>
        <li>Different APIs may differ (e.g. Processing uses left-handed)</li>
        <li>
          Converting requires axis reflection and possibly winding-order
          adjustment to keep surface normals correct
        </li>
      </ul>
    </div>
  );
};

export default CGFundamentals;
